/**
 * WHICH STRETCH OF TIME THE ACTIVITY SCREEN IS SHOWING — #296.
 *
 * WHAT WENT WRONG. The screen drew the newest 200 and announced the number it
 * was withholding — "Showing the latest 200 of 986" — on a page with no next
 * page and no filter. A cap on its own is reasonable. A cap that tells you how
 * much you are missing, and offers nothing, is an invitation with nothing
 * behind it.
 *
 * WHY DATES RATHER THAN A NEXT BUTTON. Vinnie's call, 26 August 2026, and it
 * matches how the screen is actually used: nobody looks for the four hundredth
 * most recent thing, they look for what happened last Tuesday. A Next button
 * would answer the question the count implied rather than the question people
 * arrive with.
 *
 * ⚠️ AND THE MONTHS ARE WHAT MAKE IT HONEST. Today / 7 days / 30 days alone
 * would leave anything older than a month reachable only through "All", which
 * is the capped view — the same dead end in a new coat. The months come from
 * the entries themselves, so every entry sits inside exactly one choice that
 * can be selected and read whole.
 *
 * TIME IS LOCAL, DELIBERATELY. A day boundary is where the reader is, not where
 * the server is. Vinnie is four hours behind UTC, so a UTC "today" starts at
 * eight in the evening for him — which would file his own afternoon's work
 * under yesterday.
 */

export interface Period {
  /** Stable across renders and safe in a <select>. */
  id: string;
  label: string;
  /** Inclusive lower bound, or null for "since the beginning". */
  from: number | null;
  /** Exclusive upper bound, or null for "up to now". */
  to: number | null;
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const startOfDay = (at: number): number => {
  const d = new Date(at);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

const daysBack = (now: number, days: number): number => startOfDay(now) - (days - 1) * 86_400_000;

/**
 * The choices to offer, given what there is to look at.
 *
 * `now` is passed in rather than read here so that a test can decide what day
 * it is. A function that asks the clock cannot be checked against a fixed set
 * of entries without the answers changing overnight.
 */
export function periodsFor(times: number[], now: number): Period[] {
  const out: Period[] = [
    { id: 'all', label: 'All time', from: null, to: null },
    { id: 'today', label: 'Today', from: startOfDay(now), to: null },
    { id: '7', label: 'Last 7 days', from: daysBack(now, 7), to: null },
    { id: '30', label: 'Last 30 days', from: daysBack(now, 30), to: null },
  ];
  /*
   * ONE ENTRY PER MONTH THAT HAS SOMETHING IN IT. Offering every month between
   * the first and the last would list empty ones, and a choice that leads to
   * "nothing here" is worse than not being offered.
   */
  const seen = new Set<string>();
  for (const at of times) {
    const d = new Date(at);
    const id = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    if (seen.has(id)) continue;
    seen.add(id);
    const from = new Date(d.getFullYear(), d.getMonth(), 1).getTime();
    const to = new Date(d.getFullYear(), d.getMonth() + 1, 1).getTime();
    out.push({ id, label: `${MONTHS[d.getMonth()]} ${d.getFullYear()}`, from, to });
  }
  // Newest month first, and the named ranges stay at the top where they are.
  const fixed = out.slice(0, 4);
  const months = out.slice(4).sort((a, b) => (b.from ?? 0) - (a.from ?? 0));
  return [...fixed, ...months];
}

/** Whether a moment belongs to a period. */
export function within(at: number, period: Period): boolean {
  if (period.from !== null && at < period.from) return false;
  if (period.to !== null && at >= period.to) return false;
  return true;
}

/*
 * ── `isBounded` WAS HERE, AND IT WAS THE BUG ──────────────────────────────
 *
 * It answered "can this period show everything inside it?" with "yes, if it is
 * a month" — and the screen used that to decide whether to tell somebody how to
 * reach the rest. The premise was wrong: a month is bounded in TIME, which says
 * nothing about how many entries are in it. A witness built a June holding 252
 * entries on 26 August 2026 and fifty-one of them could not be reached from any
 * choice in the picker, because the sentence about reaching them was suppressed
 * for exactly the periods that were supposed to be safe.
 *
 * Deleted rather than corrected. The screen now offers the rest of any stretch
 * that did not fit, so nothing needs to ask this question — and a helper whose
 * only reason to exist was a distinction that turned out not to hold is worth
 * removing rather than keeping in a shape nobody reads.
 */
